import { useState, useEffect, useCallback } from "react";
import { getSceneDesign, generateBlockingShot } from "@/services/sceneService";
import { get3DDesign } from "@/services/locationService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import SceneBuilder3D from "@/components/SceneBuilder3D";
import {
  Camera, Loader2, Users, X, Sparkles, Download,
  Video, Aperture, Lightbulb, Move, Eye, ArrowLeft, PersonStanding
} from "lucide-react";


const POSE_OPTIONS = [
  { value: "standing", label: "Standing" },
  { value: "sitting", label: "Sitting" },
  { value: "walking", label: "Walking" },
  { value: "pointing", label: "Pointing" },
  { value: "arms_crossed", label: "Arms Crossed" },
  { value: "hands_hips", label: "Hands on Hips" },
  { value: "gesturing", label: "Gesturing" },
  { value: "action", label: "Action" },
];

function MiniSelect({ options, value, onChange, label, icon: Icon }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-left hover:border-[var(--border-default)] transition-colors">
        {Icon && <Icon size={10} className="text-[var(--text-muted)] flex-shrink-0" />}
        <span className="flex-1 text-[10px] text-[var(--text-primary)] truncate">{selected?.label || label}</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-44 overflow-y-auto bg-[var(--bg-elevated)] border border-[var(--border-default)] rounded-lg shadow-xl">
          {options.map(opt => (
            <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-2 py-1.5 text-[10px] hover:bg-[var(--bg-hover)] ${
                opt.value === value ? 'text-[var(--color-gold-400)] bg-[var(--color-gold-500)]/10' : 'text-[var(--text-primary)]'
              }`}>{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

const SHOT_TYPES = [
  { value: "extreme-wide", label: "Extreme Wide" }, { value: "wide", label: "Wide" },
  { value: "full", label: "Full Shot" }, { value: "medium", label: "Medium" },
  { value: "medium-close", label: "Medium Close" }, { value: "close-up", label: "Close-up" },
  { value: "extreme-close", label: "Extreme Close" }, { value: "two-shot", label: "Two Shot" },
  { value: "over-shoulder", label: "Over Shoulder" }, { value: "pov", label: "POV" },
];
const CAMERA_ANGLES = [
  { value: "eye-level", label: "Eye Level" }, { value: "low-angle", label: "Low Angle" },
  { value: "high-angle", label: "High Angle" }, { value: "dutch", label: "Dutch Angle" },
  { value: "birds-eye", label: "Bird's Eye" }, { value: "worms-eye", label: "Worm's Eye" },
];
const LIGHTING_STYLES = [
  { value: "natural", label: "Natural" }, { value: "high-key", label: "High Key" },
  { value: "low-key", label: "Low Key" }, { value: "silhouette", label: "Silhouette" },
  { value: "golden-hour", label: "Golden Hour" }, { value: "rembrandt", label: "Rembrandt" },
  { value: "neon", label: "Neon" },
];
const LENS_OPTIONS = [
  { value: "24mm", label: "24mm Wide" }, { value: "35mm", label: "35mm" },
  { value: "50mm", label: "50mm Normal" }, { value: "85mm", label: "85mm Portrait" },
  { value: "135mm", label: "135mm Tele" }, { value: "anamorphic", label: "Anamorphic" },
];

const uid = () => Math.random().toString(36).substring(2, 10);

export default function ShotBlockingView({ scene, characters, projectId, anchor, scriptContext, onClose, onShotGenerated, SHOT_TYPES: parentShotTypes, CAMERA_ANGLES: parentAngles, LIGHTING_STYLES: parentLighting, LENS_OPTIONS: parentLens }) {
  const [layout, setLayout] = useState({
    room: { width: 8, depth: 8, height: 3, wall_color: "#e8e0d0", floor_color: "#8b7355" },
    props: [],
    characters: [],
    lighting: {
      ambient: { intensity: 0.4, color: "#ffffff" },
      key: { position: { x: 3, y: 4, z: 2 }, intensity: 1.0, color: "#fff5e0" },
      fill: { position: { x: -3, y: 2, z: 2 }, intensity: 0.5, color: "#e0e8ff" },
      back: { position: { x: 0, y: 3, z: -3 }, intensity: 0.3, color: "#ffffff" },
    },
  });
  const [shotPrompt, setShotPrompt] = useState("");
  const [shotType, setShotType] = useState("medium");
  const [cameraAngle, setCameraAngle] = useState("eye-level");
  const [lighting, setLighting] = useState("natural");
  const [lens, setLens] = useState("50mm");
  const [generating, setGenerating] = useState(false);
  const [generatedShot, setGeneratedShot] = useState(null);
  const [selectedObj, setSelectedObj] = useState(null);
  const [sceneDesign, setSceneDesign] = useState(null);
  const [locationRenders, setLocationRenders] = useState([]);
  const [showLightbox, setShowLightbox] = useState(null);
  const [loadingDesign, setLoadingDesign] = useState(true);

  const sceneChars = characters.filter(c =>
    scene.characters?.some(sc => sc.toLowerCase() === c.name.toLowerCase())
  );

  // Load scene design + location renders
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getSceneDesign(projectId, scene.number);
        if (data) {
          setSceneDesign(data);
          if (data.location_id) {
            const locData = await get3DDesign(data.location_id);
            if (locData) {
              setLocationRenders(locData.design_renders || []);
              setLayout(prev => ({
                ...prev,
                room: locData.room || prev.room,
                props: locData.props || [],
                lighting: locData.lighting || prev.lighting,
              }));
            }
          }
        }
      } catch { /* no design */ }

      const charEntries = sceneChars.map((c, i) => ({
        character_id: c.id,
        character_name: c.name,
        position: { x: -1 + i * 1.5, y: 0, z: 1 },
        rotation: 0,
        height: 1.7,
      }));
      setLayout(prev => ({ ...prev, characters: charEntries }));
      setLoadingDesign(false);
    };
    load();
  }, [projectId, scene.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLayoutChange = useCallback((change) => {
    setLayout(prev => {
      if (change.type === "prop") {
        return { ...prev, props: prev.props.map(p =>
          p.id === change.id ? { ...p, position: change.position } : p
        )};
      } else if (change.type === "character") {
        return { ...prev, characters: prev.characters.map(c =>
          c.character_id === change.id ? { ...c, position: change.position } : c
        )};
      }
      return prev;
    });
  }, []);

  const handlePoseChange = useCallback((characterId, pose) => {
    setLayout(prev => ({
      ...prev,
      characters: prev.characters.map(c =>
        c.character_id === characterId ? { ...c, pose } : c
      ),
    }));
  }, []);

  const captureViewport = () => window.__sceneBuilderCapture ? window.__sceneBuilderCapture() : null;

  const handleGenerate = async () => {
    if (!shotPrompt.trim()) return toast.error("Write a shot description — describe what happens in this shot");
    const viewport = captureViewport();
    if (!viewport) return toast.error("Could not capture the viewport — try again");
    setGenerating(true);
    setGeneratedShot(null);
    try {
      const data = await generateBlockingShot({
        project_id: projectId,
        scene_number: scene.number,
        anchor_id: anchor?.id || null,
        viewport_image: viewport,
        prompt: shotPrompt,
        script_context: scriptContext || anchor?.selected_text || "",
        shot_settings: { shot_type: shotType, camera_angle: cameraAngle, lighting, lens },
        character_ids: layout.characters.map(c => c.character_id),
        character_poses: layout.characters.map(c => ({ name: c.character_name, pose: c.pose || "standing" })),
      });
      setGeneratedShot(data.image_data);
      toast.success("Shot generated from 3D blocking!");
      if (onShotGenerated) onShotGenerated(data.image_data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  if (loadingDesign) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--color-gold-400)]" size={28} /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="shot-blocking-view">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button onClick={onClose} className="btn-ghost btn-icon-sm" size="sm"><ArrowLeft size={16} /></Button>
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Camera size={14} className="text-[var(--color-gold-400)]" />
              Shot Blocking — Scene {scene.number}
            </h3>
            {anchor?.selected_text && (
              <p className="text-[10px] text-[var(--text-muted)] truncate max-w-md italic">"{anchor.selected_text.slice(0, 80)}"</p>
            )}
          </div>
        </div>
        <Button onClick={handleGenerate} disabled={generating || !shotPrompt.trim()}
          className="btn-primary text-xs h-8" size="sm" data-testid="blocking-generate-btn">
          {generating ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} className="mr-1" />}
          {generating ? "Generating..." : "Generate Shot"}
        </Button>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: 3D Viewport */}
        <div className="flex-1 relative">
          <SceneBuilder3D
            layout={layout}
            onLayoutChange={handleLayoutChange}
            onSelectObject={setSelectedObj}
            selectedId={selectedObj?.id}
          />
          {/* Generating overlay */}
          {generating && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="text-center">
                <Loader2 size={32} className="animate-spin text-[var(--color-gold-400)] mx-auto mb-3" />
                <p className="text-sm text-white/80">Generating shot from blocking...</p>
                <p className="text-xs text-white/40 mt-1">This may take 15-30 seconds</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Controls + References */}
        <div className="w-72 border-l border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col">
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-4">
              {/* Shot Prompt (MANDATORY) */}
              <div>
                <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1 block">
                  Shot Description <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={shotPrompt}
                  onChange={(e) => setShotPrompt(e.target.value)}
                  placeholder="Describe what happens in this shot. E.g. 'Ravi turns to face Priya, his expression shocked. Camera slowly pushes in...'"
                  className="input text-xs resize-none h-24"
                  data-testid="blocking-shot-prompt"
                />
                <p className="text-[9px] text-[var(--text-disabled)] mt-1">
                  Script context is used for reference. Your prompt drives the shot.
                </p>
              </div>

              {/* Shot Settings */}
              <div>
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">Camera Settings</p>
                <div className="grid grid-cols-2 gap-2">
                  <MiniSelect options={SHOT_TYPES} value={shotType} onChange={setShotType} label="Shot" icon={Video} />
                  <MiniSelect options={CAMERA_ANGLES} value={cameraAngle} onChange={setCameraAngle} label="Angle" icon={Move} />
                  <MiniSelect options={LIGHTING_STYLES} value={lighting} onChange={setLighting} label="Light" icon={Lightbulb} />
                  <MiniSelect options={LENS_OPTIONS} value={lens} onChange={setLens} label="Lens" icon={Aperture} />
                </div>
              </div>

              {/* Characters in scene */}
              <div>
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">
                  <Users size={10} className="inline mr-1" />Characters & Poses
                </p>
                <div className="space-y-2">
                  {layout.characters.map((c, i) => (
                    <div key={c.character_id} className={`rounded-lg p-2 ${
                      selectedObj?.id === c.character_id ? "bg-[var(--color-gold-500)]/10 border border-[var(--color-gold-500)]/30" : "bg-[var(--bg-elevated)] border border-transparent"
                    }`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ["#E6A23C","#409EFF","#67C23A","#F56C6C"][i % 4] }}></span>
                        <span className="text-xs text-[var(--text-primary)] flex-1 truncate">{c.character_name}</span>
                      </div>
                      <div className="flex flex-wrap gap-1" data-testid={`pose-selector-${c.character_id}`}>
                        {POSE_OPTIONS.map(p => (
                          <button key={p.value} onClick={() => handlePoseChange(c.character_id, p.value)}
                            className={`text-[9px] px-1.5 py-0.5 rounded-md transition-colors ${
                              (c.pose || "standing") === p.value
                                ? "bg-[var(--color-gold-500)] text-black font-medium"
                                : "bg-[var(--bg-surface)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                            }`}
                            data-testid={`pose-${p.value}-${c.character_id}`}
                          >{p.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {layout.characters.length === 0 && (
                    <p className="text-[10px] text-[var(--text-disabled)]">No characters in this scene</p>
                  )}
                  <p className="text-[9px] text-[var(--text-disabled)]">Drag characters in the 3D view. Select a pose to adjust their stance.</p>
                </div>
              </div>

              {/* Location Reference Renders */}
              {locationRenders.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">
                    <Eye size={10} className="inline mr-1" />Location References
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {locationRenders.map((r, i) => (
                      <div key={r.angle || i} className="cursor-pointer group" onClick={() => setShowLightbox(r)}>
                        <img src={`data:image/png;base64,${r.image_data}`} alt={r.label}
                          className="w-full h-16 object-cover rounded-lg border border-[var(--border-subtle)] group-hover:border-[var(--color-gold-500)]/50 transition-all" />
                        <span className="text-[8px] text-[var(--text-disabled)]">{r.label || r.angle}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Character Costume Refs */}
              {sceneDesign?.character_costumes && Object.keys(sceneDesign.character_costumes).length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">Costume References</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(sceneDesign.character_costumes).map(([charId, data]) => {
                      if (!data.image) return null;
                      const char = characters.find(c => c.id === charId);
                      return (
                        <div key={charId} className="cursor-pointer group" onClick={() => setShowLightbox({ image_data: data.image, label: char?.name || charId })}>
                          <img src={`data:image/png;base64,${data.image}`} alt={char?.name}
                            className="w-full h-16 object-cover rounded-lg border border-[var(--border-subtle)] group-hover:border-[var(--color-gold-500)]/50 transition-all" />
                          <span className="text-[8px] text-[var(--text-disabled)]">{char?.name || "Character"}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Generated Shot */}
              {generatedShot && (
                <div>
                  <p className="text-[10px] text-[var(--color-gold-400)] uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Sparkles size={10} />Generated Shot
                  </p>
                  <div className="relative cursor-pointer group" onClick={() => setShowLightbox({ image_data: generatedShot, label: "Generated Shot" })}>
                    <img src={`data:image/png;base64,${generatedShot}`} alt="Generated shot"
                      className="w-full rounded-lg border border-[var(--color-gold-500)]/40 group-hover:border-[var(--color-gold-500)]/70 transition-all" />
                    <div className="absolute bottom-1 right-1">
                      <button onClick={(e) => {
                        e.stopPropagation();
                        const link = document.createElement("a");
                        link.href = `data:image/png;base64,${generatedShot}`;
                        link.download = `shot-scene${scene.number}.png`;
                        link.click();
                      }} className="p-1 rounded bg-black/60 text-white/70 hover:text-white">
                        <Download size={10} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!showLightbox} onOpenChange={() => setShowLightbox(null)}>
        <DialogContent className="bg-black/95 border-[var(--border-subtle)] max-w-3xl p-2">
          {showLightbox && (
            <div className="relative">
              <img src={`data:image/png;base64,${showLightbox.image_data}`} alt={showLightbox.label || "Preview"} className="w-full rounded-lg" />
              {showLightbox.label && <p className="text-center text-sm text-white/60 mt-2">{showLightbox.label}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
